import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  Alert,
  Box,
  CircularProgress,
  Card,
  CardContent,
  Collapse
} from '@mui/material';
import {
  LinkRounded,
  CheckCircleRounded,
  ExpandMoreRounded,
  ExpandLessRounded,
  DesktopWindowsRounded,
  AutoAwesomeRounded,
  LockRounded,
  PersonRounded
} from '@mui/icons-material';
import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from '../../firebase';
import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { useLinkSessionToUser, useGetSessionInfo } from '../../api/graphql_link_session';
import { useGetMyYouTubeProfiles } from '../../api/graphql_profiles';
import { CURRENT_GUARDIAN } from '../../api/graphql_guardian_name';
import { useAuthErrorHandler } from '../../hooks/useAuthErrorHandler';

interface Props {
  client: ApolloClient<NormalizedCacheObject>;
}

const firstNameOf = (fullName: string | null | undefined): string => {
  const trimmed = (fullName ?? '').trim();
  if (!trimmed) return 'this profile';
  const first = trimmed.split(/\s+/)[0];
  return first || trimmed;
};

const initialOf = (fullName: string | null | undefined): string => {
  const trimmed = (fullName ?? '').trim();
  return trimmed ? trimmed.charAt(0).toUpperCase() : '?';
};

const phoneTailOf = (user: User | null): string | null => {
  const phone = user?.phoneNumber;
  if (!phone) return null;
  const digits = phone.replace(/\D/g, '');
  return digits.length >= 4 ? digits.slice(-4) : digits || null;
};

const tokenSuffixOf = (token: string): string => {
  if (token.length <= 16) return token;
  return `…${token.slice(-12)}`;
};

export default function LinkDesktopSession({ client }: Props) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [guardianName, setGuardianName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [sessionProfile, setSessionProfile] = useState<{
    id: string;
    name: string;
    thumbnail?: string;
  } | null>(null);

  const linkSessionMutation = useLinkSessionToUser(client);
  const getSessionInfoMutation = useGetSessionInfo(client);
  const { data: profilesData, error: profilesError } = useGetMyYouTubeProfiles(client);

  useAuthErrorHandler(profilesError);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Firebase phone-auth users carry a null displayName, so pull the
  // guardian display name from our backend to render it instead of the
  // phone tail. Best-effort — falls back to the phone tail on any failure.
  useEffect(() => {
    if (!user) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await client.query({
          query: CURRENT_GUARDIAN,
          fetchPolicy: 'network-only',
        });
        const name = data?.getCurrentUser?.displayName?.trim();
        if (alive && name) setGuardianName(name);
      } catch {
        // Ignore — parentSubtitle falls back to the phone tail.
      }
    })();
    return () => {
      alive = false;
    };
  }, [user, client]);

  useEffect(() => {
    const token = searchParams.get('sessionToken');
    if (token) {
      setSessionToken(token);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!loading && !user && sessionToken) {
      navigate(`/auth?sessionToken=${encodeURIComponent(sessionToken)}`);
    }
  }, [loading, user, sessionToken, navigate]);

  // Fetch the profile this desktop session belongs to (name + avatar
  // for the confirmation card). Runs EXACTLY ONCE: the ref guard is
  // flipped synchronously before the await, so the effect re-firing
  // while the request is in flight (getSessionInfoMutation is an
  // unstable react-query handle that changes identity every render)
  // can't kick off a second call. Not gated on profilesData — the
  // card only needs session info; the profiles list loads in the
  // background and is only consumed post-link in handleLinkAccount.
  const sessionInfoStartedRef = useRef(false);
  useEffect(() => {
    if (loading || !user || !sessionToken || sessionInfoStartedRef.current) {
      return;
    }
    sessionInfoStartedRef.current = true;
    (async () => {
      try {
        const sessionInfo = await getSessionInfoMutation.mutateAsync(sessionToken);
        if (sessionInfo?.accountInfo) {
          setSessionProfile({
            id: sessionInfo.profileId || 'unknown',
            name: sessionInfo.accountInfo.name || 'Unknown Profile',
            thumbnail: sessionInfo.accountInfo.thumbnails?.[0]?.url
          });
        }
      } catch (e) {
        console.log('Could not load session info:', e);
      }
      setSessionChecked(true);
    })();
    // The ref guard — not the dep array — enforces single execution.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, sessionToken, loading]);

  const handleLinkAccount = async (): Promise<void> => {
    if (!user || !sessionToken) {
      setError('Missing authentication or session information');
      return;
    }
    setLinking(true);
    setError(null);
    try {
      const result = await linkSessionMutation.mutateAsync(sessionToken);
      if (result.success) {
        const cleanupAndGo = (path: string): void => {
          const url = new URL(window.location.href);
          url.searchParams.delete('sessionToken');
          window.history.replaceState({}, document.title, url.toString());
          navigate(path);
        };
        if (result.user?.accounts) {
          const sortedAccounts = [...result.user.accounts].sort(
            (a, b) => b.linkedAt - a.linkedAt
          );
          const sessionAccount = sortedAccounts[0];
          if (sessionAccount?.profileId) {
            const profiles = profilesData?.getMyYouTubeProfiles;
            const matchingProfile = profiles?.find((p) => p?.id === sessionAccount.profileId);
            if (matchingProfile?.id) {
              cleanupAndGo(`/profile/${matchingProfile.id}/report`);
              return;
            }
          }
        }
        cleanupAndGo('/profile');
      } else {
        setError(result.message || 'Failed to link account');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to link account');
    } finally {
      setLinking(false);
    }
  };

  const handleCancel = (): void => {
    const url = new URL(window.location.href);
    url.searchParams.delete('sessionToken');
    window.history.replaceState({}, document.title, url.toString());
    navigate('/profile');
  };

  if (loading || !sessionChecked) {
    return <LinkPageLoader label="Checking your session…" />;
  }

  if (!sessionToken) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Card sx={{ borderRadius: 4, border: '1px solid #E2E8F0', boxShadow: 'none' }}>
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#0F172A' }} gutterBottom>
              Invalid link
            </Typography>
            <Typography sx={{ color: '#64748B', mb: 3 }}>
              This link appears to be invalid or expired. Try again from the Desktop app.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate('/profile')}
              sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 700 }}
            >
              Go to profiles
            </Button>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (!user) {
    return <LinkPageLoader label="Redirecting to sign-in…" />;
  }

  const kidName = sessionProfile?.name ?? 'this profile';
  const kidFirst = firstNameOf(sessionProfile?.name);
  const kidInitial = initialOf(sessionProfile?.name);
  const phoneTail = phoneTailOf(user);
  const parentSubtitle =
    guardianName ?? (phoneTail ? `••• ${phoneTail}` : user.email ?? 'Authenticated');

  const linkingBullets = [
    `${kidName}'s profile appears in your Profiles dashboard`,
    `Her watch history and recommendations sync to your account`,
    `You can score videos and take corrective actions from web or mobile`
  ];

  return (
    <Box sx={{ bgcolor: '#F8FAFC', minHeight: '100%', py: { xs: 5, md: 7 } }}>
      <Container maxWidth="sm" sx={{ px: { xs: 2, sm: 3 } }}>
        {/* Main card */}
        <Box
          sx={{
            bgcolor: '#FFFFFF',
            borderRadius: '24px',
            border: '1px solid #E2E8F0',
            boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
            p: { xs: 3, sm: 4 }
          }}
        >
          {/* Eyebrow chip */}
          <Box sx={{ textAlign: 'center' }}>
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                bgcolor: '#E0E7FF',
                color: '#4338CA',
                borderRadius: 999,
                px: 1.5,
                py: 0.5,
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: '0.08em',
                textTransform: 'uppercase'
              }}
            >
              <AutoAwesomeRounded sx={{ fontSize: 12 }} />
              New Desktop session
            </Box>
          </Box>

          {/* Error */}
          {error && (
            <Alert severity="error" sx={{ mt: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {/* Connection visual — a 3-column CSS grid spanning the full
              card width. The two side columns are `minmax(0, 1fr)` so
              they are ALWAYS equal width regardless of how long the
              profile names are; the centre column is `auto` (the 40px
              link icon). Equal side columns + justifyItems:center keep
              the avatars symmetric around the icon — a long kid name
              can't shove the icon off-centre. alignItems:center keeps
              the 40px icon vertically centred on the 64px avatars. */}
          <Box
            sx={{
              mt: 3,
              display: 'grid',
              width: '100%',
              gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
              justifyItems: 'center',
              alignItems: 'center',
              columnGap: 2,
              rowGap: 1
            }}
          >
            {/* Row 1 — parent avatar */}
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: '#E0E7FF',
                color: '#4F46E5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 1px 2px rgba(15,23,42,0.06)'
              }}
            >
              <PersonRounded sx={{ fontSize: 28 }} />
            </Box>

            {/* Row 1 — link icon */}
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                border: '2px solid #A7F3D0',
                bgcolor: '#ECFDF5',
                color: '#059669',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 1px 2px rgba(15,23,42,0.06)'
              }}
            >
              <LinkRounded sx={{ fontSize: 16 }} />
            </Box>

            {/* Row 1 — kid avatar */}
            {sessionProfile?.thumbnail ? (
              <Box
                component="img"
                src={sessionProfile.thumbnail}
                alt={kidName}
                sx={{
                  display: 'block',
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  objectFit: 'cover',
                  boxShadow: '0 1px 2px rgba(15,23,42,0.06)'
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #FDA4AF 0%, #F43F5E 100%)',
                  color: '#FFFFFF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 22,
                  fontWeight: 800,
                  boxShadow: '0 1px 2px rgba(15,23,42,0.06)'
                }}
              >
                {kidInitial}
              </Box>
            )}

            {/* Row 2 — parent label */}
            <Box sx={{ textAlign: 'center', width: '100%', minWidth: 0 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0F172A' }}>
                Your account
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  fontSize: 10,
                  color: '#64748B'
                }}
              >
                {parentSubtitle}
              </Typography>
            </Box>

            {/* Row 2 — spacer under the link icon */}
            <Box />

            {/* Row 2 — kid label */}
            <Box sx={{ textAlign: 'center', width: '100%', minWidth: 0 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0F172A' }}>
                {kidName}'s YouTube
              </Typography>
              <Typography sx={{ fontSize: 10, color: '#64748B' }}>via Desktop</Typography>
            </Box>
          </Box>

          {/* Title + subtitle */}
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography
              component="h1"
              sx={{
                fontSize: { xs: 22, sm: 24 },
                fontWeight: 800,
                lineHeight: 1.2,
                letterSpacing: '-0.01em',
                color: '#0F172A'
              }}
            >
              Link {kidName}'s YouTube to your account?
            </Typography>
            <Typography
              sx={{
                mt: 1,
                mx: 'auto',
                maxWidth: 420,
                fontSize: 14,
                lineHeight: 1.55,
                color: '#475569'
              }}
            >
              We noticed your Desktop signed {kidFirst} in to YouTube. Linking lets you review her
              feed and take action from any device.
            </Typography>
          </Box>

          {/* When you link */}
          <Box
            sx={{
              mt: 3.5,
              borderRadius: '16px',
              border: '1px solid #E2E8F0',
              bgcolor: '#F8FAFC',
              p: 2.5
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: '#64748B'
              }}
            >
              When you link
            </Typography>
            <Box component="ul" sx={{ m: 0, mt: 1.5, p: 0, listStyle: 'none' }}>
              {linkingBullets.map((line, i) => (
                <Box
                  component="li"
                  key={i}
                  sx={{
                    display: 'flex',
                    gap: 1.25,
                    alignItems: 'flex-start',
                    py: 0.6
                  }}
                >
                  <CheckCircleRounded
                    sx={{ fontSize: 18, color: '#10B981', mt: '2px', flexShrink: 0 }}
                  />
                  <Typography sx={{ fontSize: 14, lineHeight: 1.55, color: '#334155' }}>
                    {line}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Actions */}
          <Box
            sx={{
              mt: 3,
              display: 'flex',
              flexDirection: { xs: 'column-reverse', sm: 'row' },
              justifyContent: 'flex-end',
              gap: 1.25
            }}
          >
            <Button
              onClick={handleCancel}
              disabled={linking}
              sx={{
                borderRadius: 999,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: 14,
                px: 2.5,
                py: 1.1,
                color: '#334155',
                border: '1px solid #E2E8F0',
                bgcolor: '#FFFFFF',
                '&:hover': { borderColor: '#CBD5E1', bgcolor: '#F8FAFC' }
              }}
            >
              Not now
            </Button>
            <Button
              onClick={handleLinkAccount}
              disabled={linking}
              startIcon={
                linking ? (
                  <CircularProgress size={14} sx={{ color: 'inherit' }} />
                ) : (
                  <LinkRounded sx={{ fontSize: 16 }} />
                )
              }
              sx={{
                borderRadius: 999,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: 14,
                px: 2.5,
                py: 1.1,
                bgcolor: '#4F46E5',
                color: '#FFFFFF',
                boxShadow: 'none',
                '&:hover': { bgcolor: '#4338CA', boxShadow: 'none' },
                '&.Mui-disabled': { bgcolor: '#A5B4FC', color: '#FFFFFF' }
              }}
            >
              {linking ? 'Linking…' : `Link ${kidFirst}'s profile`}
            </Button>
          </Box>

          {/* Inline privacy line */}
          <Box
            sx={{
              mt: 2,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 0.75,
              color: '#94A3B8'
            }}
          >
            <LockRounded sx={{ fontSize: 12 }} />
            <Typography sx={{ fontSize: 11, color: '#94A3B8' }}>
              Stored privately in your account. Unlink any time from Profile settings.
            </Typography>
          </Box>
        </Box>

        {/* Session details pill (outside the card) */}
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Button
            onClick={() => setDetailsOpen((v) => !v)}
            startIcon={<DesktopWindowsRounded sx={{ fontSize: 14 }} />}
            endIcon={
              detailsOpen ? (
                <ExpandLessRounded sx={{ fontSize: 14 }} />
              ) : (
                <ExpandMoreRounded sx={{ fontSize: 14 }} />
              )
            }
            sx={{
              borderRadius: 999,
              textTransform: 'none',
              fontWeight: 700,
              fontSize: 12,
              px: 1.75,
              py: 0.75,
              color: '#64748B',
              bgcolor: '#FFFFFF',
              border: '1px solid #E2E8F0',
              '&:hover': { borderColor: '#CBD5E1', color: '#334155', bgcolor: '#F8FAFC' }
            }}
          >
            Session details
          </Button>
          <Collapse in={detailsOpen}>
            <Box
              sx={{
                mt: 1.5,
                mx: 'auto',
                maxWidth: 440,
                borderRadius: '12px',
                border: '1px solid #E2E8F0',
                bgcolor: '#F8FAFC',
                p: 2,
                textAlign: 'left'
              }}
            >
              <Box component="dl" sx={{ m: 0 }}>
                <DetailRow label="Device" value="Desktop" />
                {sessionProfile && <DetailRow label="Profile" value={sessionProfile.name} />}
                <DetailRow
                  label="Session token"
                  value={tokenSuffixOf(sessionToken)}
                  mono
                />
              </Box>
              <Typography sx={{ mt: 1.5, fontSize: 10, color: '#94A3B8' }}>
                If you didn't initiate this session, tap "Not now" and check your Desktop
                installations from Settings.
              </Typography>
            </Box>
          </Collapse>
        </Box>
      </Container>
    </Box>
  );
}

function LinkPageLoader({ label }: { label: string }): JSX.Element {
  return (
    <Box
      sx={{
        bgcolor: '#F8FAFC',
        minHeight: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 12
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2
        }}
      >
        <CircularProgress size={34} thickness={4} sx={{ color: '#4F46E5' }} />
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#64748B' }}>
          {label}
        </Typography>
      </Box>
    </Box>
  );
}

function DetailRow({
  label,
  value,
  mono
}: {
  label: string;
  value: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.4 }}>
      <Box component="dt" sx={{ fontSize: 12, color: '#64748B' }}>
        {label}
      </Box>
      <Box
        component="dd"
        sx={{
          m: 0,
          fontSize: 12,
          fontWeight: 500,
          color: '#334155',
          fontFamily: mono
            ? 'ui-monospace, SFMono-Regular, Menlo, monospace'
            : 'inherit',
          maxWidth: '60%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
      >
        {value}
      </Box>
    </Box>
  );
}
